import { resolveTemplateIdByVariantId } from '../../../../../shared/view/uv-inserts/resolve-template-id-by-variant-id';

/**
 * Builds a choices array from selections for use with buildPersonalisationChoices.
 * This is extracted from PersonalisationContext.jsx to allow reuse in tests.
 *
 * @param {Object} params
 * @param {Object} params.selections - The selections object containing slots.
 * @param {Map} params.variants - Map of variant IDs to variant objects.
 * @param {import('@shared/view/uv-inserts/resolve-uv-inserts').ResolvedUvInserts | null} [params.resolvedUvInserts] - Resolved UV inserts data including templateIds map.
 * @returns {Array} Array of choice payloads.
 */
export function buildChoicesFromSelections({ selections, variants, resolvedUvInserts = null }) {
  const choices = [];
  const toSafeString = (value) => (typeof value === 'string' ? value : value ? String(value) : '');

  selections.slots
    .filter(({ selected }) => selected !== null && selected !== undefined)
    .forEach(
      ({
        componentGroupId,
        selected,
        id,
        slotType,
        lineItemPropertyKey,
        translatedLineItemPropertyKey,
        stepId,
        slotMode,
        groupLineItemPropertyKey,
        extraImages,
        visualisationGroup,
        supportingImage,
        supportingImageLineItemPropertyKey
      }) => {
        // For GROUPED IMAGE_UPLOAD slots, images are propagated to every group slot so the
        // user doesn't have to re-upload when switching styles. Those propagated copies are
        // marked with groupLineItemPropertyKey === null. Only the active group's slot (which
        // has a non-null groupLineItemPropertyKey) should be submitted to the cart.
        // Without this filter, imagePropertiesProcessing receives multiple IMAGE_UPLOAD
        // choices and its single-choice fallback path never fires, so the image is dropped.
        if (slotMode === 'GROUPED' && slotType === 'IMAGE_UPLOAD' && !groupLineItemPropertyKey) return;
        // For GROUPED IMAGE_UPLOAD, the active group name is the line item property key.
        const effectiveLineItemPropertyKey = toSafeString(
          slotMode === 'GROUPED' && slotType === 'IMAGE_UPLOAD' && groupLineItemPropertyKey
            ? groupLineItemPropertyKey
            : slotType === 'ALPHANUMERIC' && !lineItemPropertyKey
              ? 'text'
              : lineItemPropertyKey
        );
        const effectiveTranslatedKey = toSafeString(translatedLineItemPropertyKey);

        if (Array.isArray(selected)) {
          // For upsells with multiple selections, create a choice for each selected variant
          selected.forEach((variantId, index) => {
            // Skip null or undefined variantIds
            if (!variantId) return;
            const variant = variants.get(variantId);
            const effectiveComponentGroupId = componentGroupId || variant?.componentGroupId || null;

            choices.push({
              componentGroupId: effectiveComponentGroupId,
              variantId: toSafeString(variantId),
              slotId: id,
              stepId,
              lineItemPropertyKey: effectiveLineItemPropertyKey,
              translatedLineItemPropertyKey: effectiveTranslatedKey,
              lineItemPropertyKeyValue: toSafeString(variantId),
              slotType,
              slotMode,
              index
            });
          });
        } else {
          // Skip null or undefined selected values
          if (!selected) return;

          // Handle ALPHANUMERIC slots differently - extract text and font
          if (slotType === 'ALPHANUMERIC' && typeof selected === 'object' && selected.text) {
            choices.push({
              componentGroupId: null, // ALPHANUMERIC slots don't have component groups
              variantId: selected.text, // Use the text as the variantId
              slotId: id,
              stepId,
              lineItemPropertyKey: effectiveLineItemPropertyKey,
              translatedLineItemPropertyKey,
              lineItemPropertyKeyValue: selected.text, // The actual text content
              slotType,
              slotMode,
              font: selected.font, // Add the font selection
              visualisationGroup: visualisationGroup?.groupId || null, // Pass visualisationGroup.groupId for matching visData
              supportingImageLineItemPropertyKey:
                visualisationGroup?.supportingImageLineItemPropertyKey || supportingImageLineItemPropertyKey || null // Pass supporting image line item property key for warehouse use (from visualisationGroup or slot)
            });
          } else if (selected instanceof File) {
            // Handle IMAGE_UPLOAD slots - pass the File object as lineItemPropertyKeyValue
            choices.push({
              componentGroupId: null, // IMAGE_UPLOAD slots don't have component groups
              variantId: '', // Force string to satisfy downstream processing
              slotId: id,
              stepId,
              lineItemPropertyKey: effectiveLineItemPropertyKey,
              translatedLineItemPropertyKey: effectiveTranslatedKey,
              lineItemPropertyKeyValue: selected, // Pass the File object (edited image)
              slotType,
              slotMode,
              extraImages: extraImages || [], // Include extraImages for IMAGE_UPLOAD slots
              visualisationGroup: visualisationGroup?.groupId || null,
              supportingImage: supportingImage || null,
              supportingImageLineItemPropertyKey:
                visualisationGroup?.supportingImageLineItemPropertyKey || supportingImageLineItemPropertyKey || null
            });
          } else if (slotType === 'UV_INSERT' && typeof selected === 'object' && selected !== null) {
            // UV_INSERT: selected is a UvInsertSlotSelection object { variantId, templateId, configHandle, textByKey, imageByKey }.
            // Always source templateId from resolvedUvInserts.templateIds to avoid stale values in selection state.
            const resolvedTemplateId = resolveTemplateIdByVariantId({
              templateIds: resolvedUvInserts?.templateIds,
              variantId: selected.variantId
            });
            const uvImageByKey = Object.entries(selected.imageByKey || {}).reduce((acc, [key, value]) => {
              if (value instanceof File) {
                acc[key] = value;
              }
              return acc;
            }, {});

            choices.push({
              componentGroupId: null,
              variantId: String(selected.variantId || ''),
              slotId: id,
              stepId,
              lineItemPropertyKey: null,
              translatedLineItemPropertyKey: null,
              lineItemPropertyKeyValue: null,
              slotType,
              slotMode,
              uvInsertSelection: {
                ...selected,
                templateId: resolvedTemplateId,
                imageByKey: uvImageByKey
              }
            });
          } else {
            // For single selections, create one choice
            const variant = variants.get(selected);
            const effectiveComponentGroupId = componentGroupId || variant?.componentGroupId || null;
            choices.push({
              componentGroupId: effectiveComponentGroupId,
              variantId: toSafeString(selected),
              slotId: id,
              stepId,
              lineItemPropertyKey: effectiveLineItemPropertyKey,
              translatedLineItemPropertyKey: effectiveTranslatedKey,
              lineItemPropertyKeyValue: toSafeString(selected),
              slotType,
              slotMode
            });
          }
        }
      }
    );

  return choices;
}
